import React from 'react';
import {defineMessages, type MessageDescriptor} from 'react-intl';
import {
    DateRangeFilter,
    type DateRangeFilterValue,
    FilterGroupNew,
    type FilterGroupNewFilter,
    type FilterProps,
    type FiltersProps,
    type MultiKeyFilterTextValue,
    type QueryFilterAdapterModel,
    type QueryFilterAdapterProps,
    TextInputWithDropdownFilter,
    withQueryFilter,
} from '@igp/shared/components/filter';
import type {AgentFilterSharedProps} from '@igp/shared/features/agent-profile';
import {AgentFilter, type AgentFilterValue} from '@igp/shared/features/agent-profile';
import {CountryFilter} from '@igp/shared/features/country';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';
import {getMirrorObject} from '@igp/shared/utils';
import {EntityType} from '@models';

import {firstAvailableDate} from '../constants';
import type {
    PlayerEcologyWeeklyFilterKeys,
    PlayerEcologyWeeklyFilterName,
    PlayerEcologyWeeklyFilterViewModel,
    PlayerEcologyWeeklyFilterViewModelTextKeys,
    PlayerEcologyWeeklyTextFilterKeys,
} from '../types';

const localized = defineMessages({
    player_uid_username: {
        id: 'PlayerEcologyWeeklyQueryFilters_player_uid_username',
        defaultMessage: 'All',
    },
    player_uid: {
        id: 'PlayerEcologyWeeklyQueryFilters_player_uid',
        defaultMessage: 'Player ID',
    },
    player_username: {
        id: 'PlayerEcologyWeeklyQueryFilters_player_username',
        defaultMessage: 'Player Username',
    },
    numberPlaceholder: {
        id: 'PlayerEcologyWeeklyQueryFilters_numberPlaceholder',
        defaultMessage: 'For example, 123',
    },
    namePlaceholder: {
        id: 'PlayerEcologyWeeklyQueryFilters_namePlaceholder',
        defaultMessage: 'For example, Antonio',
    },
    countries: {
        id: 'PlayerEcologyWeeklyQueryFilters_countries',
        defaultMessage: 'Country',
    },
});

function PlayerEcologyWeeklyFilters({
    model,
    onChange,
    availableFilters,
    defaultPlayerId,
    viewType,
    isAgent,
}: FiltersProps<PlayerEcologyWeeklyFilterViewModel, PlayerEcologyWeeklyFilterName> & AgentFilterSharedProps<EntityType>) {
    const {locale} = useLocale();

    const availableDateRangeForSelection = {from: firstAvailableDate.toDate()};

    function getTextFilterPlaceholder(option: PlayerEcologyWeeklyFilterViewModelTextKeys) {
        const placeholderMapper: Record<PlayerEcologyWeeklyFilterViewModelTextKeys, MessageDescriptor> = {
            player_uid_username: localized.numberPlaceholder,
            player_uid: localized.numberPlaceholder,
            player_username: localized.namePlaceholder,
        };
        return placeholderMapper[option];
    }

    const filters: FilterGroupNewFilter<PlayerEcologyWeeklyFilterViewModel, PlayerEcologyWeeklyFilterName>[] = [
        {
            filterName: 'text',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<PlayerEcologyWeeklyFilterViewModelTextKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.player_uid_username, value: 'player_uid_username'},
                        {label: localized.player_uid, value: 'player_uid'},
                        {label: localized.player_username, value: 'player_username'},
                    ]}
                    getInputPlaceholder={getTextFilterPlaceholder}
                    width="full"
                    {...props}
                />
            ),
        },
        {
            filterName: 'countries',
            modelField: 'countries',
            component: (props: FilterProps<string[]>) => (
                <CountryFilter {...props} inputLabel={localized.countries} countryEntity={EntityType.Country} />
            ),
        },
        {
            filterName: 'reportWeekDate',
            modelField: 'reportWeekDate',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    width="full"
                    locale={locale as Locale}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                    filterEntity="report"
                    hasInputTooltip={true}
                />
            ),
        },
        {
            filterName: 'agent',
            modelField: 'agent',
            component: (props: FilterProps<AgentFilterValue>) => (
                <AgentFilter
                    defaultPlayerId={defaultPlayerId}
                    width="full"
                    viewType={viewType}
                    withDownstream={true}
                    agentProfileEntity={EntityType.AgentProfile}
                    isTreeMode={isAgent}
                    {...props}
                />
            ),
        },
    ];

    return (
        <FilterGroupNew
            mode="update-on-change"
            allFilters={filters}
            model={model}
            onChange={onChange}
            availableFilters={availableFilters}
        />
    );
}

type PlayerEcologyWeeklyFiltersAdapterProps = QueryFilterAdapterProps<
    PlayerEcologyWeeklyFilterKeys,
    PlayerEcologyWeeklyFilterName,
    PlayerEcologyWeeklyTextFilterKeys
>;

function PlayerEcologyWeeklyQueryFiltersAdapter({model, onChange, ...props}: PlayerEcologyWeeklyFiltersAdapterProps) {
    const viewModelTextKeyToQueryTextKey: Record<PlayerEcologyWeeklyFilterViewModelTextKeys, PlayerEcologyWeeklyTextFilterKeys> = {
        player_uid_username: 'player_uid_username',
        player_uid: 'player_uid',
        player_username: 'player_username',
    };
    const queryTextKeyToViewModelTextKey: Record<PlayerEcologyWeeklyTextFilterKeys, PlayerEcologyWeeklyFilterViewModelTextKeys> =
        getMirrorObject(viewModelTextKeyToQueryTextKey);

    const filterModel: PlayerEcologyWeeklyFilterViewModel = {
        text: model?.text
            ? {
                  text: model.text.text,
                  option: queryTextKeyToViewModelTextKey[model?.text?.option],
              }
            : null,
        countries: model?.country as string[],
        reportWeekDate: {
            from: model?.report_week_date_from as number,
            to: model?.report_week_date_to as number,
        },
        agent: {
            agentId: model?.referrer_id as string,
            isDownstream: model?.is_downstream as boolean,
        },
    };

    function handleChange(model: PlayerEcologyWeeklyFilterViewModel) {
        const result: QueryFilterAdapterModel<PlayerEcologyWeeklyFilterKeys, PlayerEcologyWeeklyTextFilterKeys> = {
            text: model.text
                ? {
                      text: model.text.text,
                      option: viewModelTextKeyToQueryTextKey[model.text.option],
                  }
                : undefined,
            country: model.countries,
            report_week_date_from: model.reportWeekDate?.from,
            report_week_date_to: model.reportWeekDate?.to,
            referrer_id: model?.agent?.agentId,
            is_downstream: model?.agent?.isDownstream,
        };

        onChange(result);
    }

    return <PlayerEcologyWeeklyFilters model={filterModel} onChange={handleChange} {...props} />;
}

export const PlayerEcologyWeeklyQueryFilters = withQueryFilter<
    PlayerEcologyWeeklyFilterKeys,
    PlayerEcologyWeeklyFilterName,
    PlayerEcologyWeeklyTextFilterKeys,
    AgentFilterSharedProps<string>
>(PlayerEcologyWeeklyQueryFiltersAdapter, {text: ['player_uid_username', 'player_uid', 'player_username']});
